import React, { useState, useEffect } from 'react';
import {
  Search, Filter, Eye, FileText, Calendar, User, Building2,
  Package, CheckCircle, XCircle, Clock, Download, X,
  ChevronLeft, ChevronRight, Printer, ChevronDown, AlertCircle, Trash2, Upload, RotateCcw, Users
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import megalogo from '../Image/megalogo.png';

export default function MaterialRequestList({ setCurrentPage }) {
  const [mrList, setMrList] = useState([]);
  const [filteredMRs, setFilteredMRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [selectedMR, setSelectedMR] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [paginationPage, setPaginationPage] = useState(1); const [itemsPerPage] = useState(10);

  // ✅ LOGO AND WEBSITE INFO STATES
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const [showLogoUploader, setShowLogoUploader] = useState(false);

  // ✅ NEW: Grouped MRs and Selected Name
  const [groupedMRs, setGroupedMRs] = useState([]);
  const [selectedNameForPDF, setSelectedNameForPDF] = useState(null);

  useEffect(() => {
    fetchMRList();
    fetchWebsiteInfo();
  }, []);

  useEffect(() => {
    filterMRs();
  }, [searchTerm, statusFilter, departmentFilter, mrList]);

  const fetchMRList = async () => {
    setLoading(true);
    try {
      const headerRes = await fetch(`${API_BASE_URL}api/MRHeader`);

      if (!headerRes.ok) {
        setMrList([]);
        setFilteredMRs([]);
        return;
      }

      const headerData = await headerRes.json();
      const headerArray = Array.isArray(headerData) ? headerData : (headerData ? [headerData] : []);

      const mrListWithDetails = await Promise.all(
        headerArray.map(async (header) => {
          try {
            const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${header.id}`);
            if (detailsRes.ok) {
              const details = await detailsRes.json();
              return { ...header, mrDetails: Array.isArray(details) ? details : [] };
            }
            return { ...header, mrDetails: [] };
          } catch (err) {
            return { ...header, mrDetails: [] };
          }
        })
      );

      setMrList(mrListWithDetails);
      setFilteredMRs(mrListWithDetails);

      // ✅ GROUP MRs after fetching
      groupMRsByDeptAndControl(mrListWithDetails);
    } catch (error) {
      console.error('❌ Error fetching MR list:', error);
      setMrList([]);
      setFilteredMRs([]);
    } finally {
      setLoading(false);
    }
  };



  // ✅ KEEP THESE STATES (Lines 100-101)
  const [sparepartsUsers, setSparepartsUsers] = useState([]);
  const [selectedReleasedBy, setSelectedReleasedBy] = useState('');

  // 🔥 UPDATED: Fetch users WITH digital signatures
  const fetchSparepartsUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}api/Users`);
      if (response.ok) {
        const users = await response.json();
        const spareparts = users.filter(user =>
          user.authorized?.toLowerCase() === 'spareparts' && user.isActive === 1
        );

        // 🔥 FETCH DIGITAL SIGNATURE FOR EACH USER
        const usersWithSignatures = await Promise.all(
          spareparts.map(async (user) => {
            try {
              // Fetch digital signature
              const signResponse = await fetch(`${API_BASE_URL}api/Users/${user.id}/digitalsign`);

              if (signResponse.ok) {
                const blob = await signResponse.blob();
                const base64 = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(blob);
                });

                return {
                  ...user,
                  digitalSignFileBase64: base64.split(',')[1], // Get base64 without prefix
                  digitalSignContentType: blob.type || user.digitalSignContentType
                };
              }

              return user; // Return user without signature if fetch fails
            } catch (err) {
              console.warn(`Failed to fetch signature for user ${user.id}:`, err);
              return user;
            }
          })
        );

        setSparepartsUsers(usersWithSignatures);
        console.log('✅ Spareparts users loaded with signatures:', usersWithSignatures.length);
      }
    } catch (error) {
      console.error('Error fetching spareparts users:', error);
    }
  };

  // ✅ EXISTING useEffect
  useEffect(() => {
    fetchMRList();
    fetchWebsiteInfo();
    fetchSparepartsUsers(); // ← DAGDAG MO LANG 'TO!
  }, []);










  // 🔥 RETURN ITEM STATES - DAGDAG MO TO SA TAAS (after line 40)
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedMRForReturn, setSelectedMRForReturn] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [returningItems, setReturningItems] = useState(false);

  // 🔥 RETURN FUNCTIONS - DAGDAG MO TO BEFORE return statement

  const handleOpenReturnModal = async (mr) => {
    try {
      const headerRes = await fetch(`${API_BASE_URL}api/MRHeader/${mr.id}`);
      if (!headerRes.ok) throw new Error('Failed to load MR');

      const headerData = await headerRes.json();
      const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mr.id}`);
      let mrDetails = [];
      if (detailsRes.ok) mrDetails = await detailsRes.json();

      setSelectedMRForReturn({
        ...headerData,
        mrDetails: Array.isArray(mrDetails) ? mrDetails : []
      });

      const initReturnItems = mrDetails.map(item => ({
        ...item,
        returnQty: 0,
        returnReason: ''
      }));

      setReturnItems(initReturnItems);
      setShowReturnModal(true);
    } catch (error) {
      console.error('Error loading MR for return:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load material request details',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const updateReturnQty = (itemId, qty) => {
    setReturnItems(returnItems.map(item =>
      item.id === itemId
        ? { ...item, returnQty: Math.min(Math.max(0, parseFloat(qty) || 0), item.requestedQty - (item.returnedQty || 0)) }
        : item
    ));
  };

  const updateReturnReason = (itemId, reason) => {
    setReturnItems(returnItems.map(item =>
      item.id === itemId ? { ...item, returnReason: reason } : item
    ));
  };

  const handleSubmitReturns = async () => {
    const itemsToReturn = returnItems.filter(item => item.returnQty > 0);

    if (itemsToReturn.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Items Selected',
        text: 'Please enter quantity for items you want to return',
        confirmButtonColor: '#f59e0b'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Submit Return Request?',
      html: `
        <div class="text-left">
          <p class="mb-2"><strong>MR No:</strong> ${selectedMRForReturn.mrNo}</p>
          <p class="mb-2"><strong>Control No:</strong> ${selectedMRForReturn.controlNo}</p>
          <p class="mb-2"><strong>Items to Return:</strong> ${itemsToReturn.length}</p>
          <p class="mt-4 text-orange-600 font-semibold">⚠️ Return request will be sent for approval</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#ea580c',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Submit Returns',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setReturningItems(true);

    try {
      for (const item of itemsToReturn) {

        // ✅ STEP 1 — Update returnedQty sa MRDetails lang
        const updatePayload = {
          id: item.id,
          mrHeaderId: item.mrHeaderId,
          itemCode: item.itemCode,
          itemDescription: item.itemDescription,
          requestedQty: item.requestedQty,
          issuedQty: item.issuedQty,
          uom: item.uom,
          warehouseCode: item.warehouseCode,
          warehouseId: item.warehouseId,
          batchNo: item.batchNo || '',
          remarks: `${item.remarks || ''}\n[RETURN SUBMITTED] ${new Date().toISOString()} - Qty: ${item.returnQty} ${item.uom} - Reason: ${item.returnReason || 'No reason provided'}`.trim(),
          status: item.status,
          purpose: item.purpose || '',
          returnedQty: (item.returnedQty || 0) + item.returnQty,
          inventoryDataId: item.inventoryDataId
        };

        const res = await fetch(`${API_BASE_URL}api/MRDetails/update/${item.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatePayload)
        });

        if (!res.ok) throw new Error(`Failed to update MRDetails for ${item.itemCode}`);

        // ✅ STEP 2 — Create ReturnRequest record (Pending — hintayin approval)
        const user = JSON.parse(localStorage.getItem("user") || '{}');
        const requestedByName = user.firstName
          ? `${user.firstName} ${user.lastName}`
          : user.username || 'User';

        await fetch(`${API_BASE_URL}api/ReturnRequest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mrHeaderId: item.mrHeaderId,
            inventoryDataId: item.inventoryDataId,
            itemCode: item.itemCode,
            itemDescription: item.itemDescription,
            warehouseCode: item.warehouseCode,
            requestedReturnQty: item.returnQty,
            returnReason: item.returnReason || '',
            requestedBy: user?.userId || 1,
            requestedByName: requestedByName,
            requestedDate: new Date().toISOString(),
            mrControlNo: selectedMRForReturn.controlNo || selectedMRForReturn.mrNo,
            status: 0  // ✅ Pending — hindi pa nag-aapprove
          })
        });

        // ❌ WALA NANG INVENTORY UPDATE DITO
        // Inventory ay ia-update ONLY sa handleApprove ng ReturnRequestList
        // para hindi mag-double add
      }

      // ✅ STEP 3 — Log
      const user = JSON.parse(localStorage.getItem("user") || '{}');
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "CREATE",
          module: "Material Request",
          description: `Return request submitted for MR ${selectedMRForReturn.mrNo} (${selectedMRForReturn.controlNo}) - ${itemsToReturn.length} item(s): ${itemsToReturn.map(i => `${i.itemCode} x${i.returnQty}`).join(', ')}`,
          userId: user?.userId,
          username: user?.username,
          fullName: user?.fullName || user?.username,
        })
      }).catch(err => console.error("Log failed:", err));

      await Swal.fire({
        icon: 'success',
        title: 'Return Request Submitted!',
        html: `
          <p><strong>${itemsToReturn.length}</strong> item(s) submitted for approval</p>
          <p class="mt-2 text-sm text-gray-500">Waiting for admin approval before inventory is updated</p>
        `,
        confirmButtonColor: '#10b981',
        timer: 2500
      });

      setShowReturnModal(false);
      setSelectedMRForReturn(null);
      setReturnItems([]);
      fetchMRList();

    } catch (error) {
      console.error('❌ Error submitting return request:', error);
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: error.message || 'Failed to submit return request',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setReturningItems(false);
    }
  };












  // ========================================
  // STEP 1: DAGDAG SA STATES (malapit sa ibang states mo)
  // ========================================
  const [expandedRows, setExpandedRows] = useState({});        // Track kung aling rows ang naka-expand
  const [returnHistory, setReturnHistory] = useState({});      // Store return history per MR
  const [loadingHistory, setLoadingHistory] = useState({});    // Loading state per row

  // ========================================
  // STEP 2: DAGDAG NA FUNCTION (bago ang return statement)
  // ========================================
  const toggleReturnHistory = async (mrId) => {
    // Kung naka-expand na, i-collapse
    if (expandedRows[mrId]) {
      setExpandedRows(prev => ({ ...prev, [mrId]: false }));
      return;
    }

    // I-expand at i-fetch ang return history
    setExpandedRows(prev => ({ ...prev, [mrId]: true }));

    // Kung na-fetch na, huwag na mag-fetch ulit
    if (returnHistory[mrId]) return;

    setLoadingHistory(prev => ({ ...prev, [mrId]: true }));

    try {
      // Fetch return requests para sa MR na ito
      const res = await fetch(`${API_BASE_URL}api/ReturnRequest`);
      if (res.ok) {
        const allReturns = await res.json();
        // I-filter lang yung para sa current MR
        const mrReturns = allReturns.filter(r => r.mrHeaderId === mrId);
        setReturnHistory(prev => ({ ...prev, [mrId]: mrReturns }));
      }
    } catch (err) {
      console.error('Error fetching return history:', err);
      setReturnHistory(prev => ({ ...prev, [mrId]: [] }));
    } finally {
      setLoadingHistory(prev => ({ ...prev, [mrId]: false }));
    }
  };

  const getReturnStatusBadge = (status) => {
    if (status === 1) return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold">Approved</span>;
    if (status === -1) return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">Rejected</span>;
    return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">Pending</span>;
  };





  // 🔥 NEW: Fetch digital signature by user ID
  const fetchUserSignature = async (userId) => {
    if (!userId) return null;

    try {
      const signResponse = await fetch(`${API_BASE_URL}api/Users/${userId}/digitalsign`);

      if (signResponse.ok) {
        const blob = await signResponse.blob();
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

        return base64; // Full data URL with prefix
      }

      return null;
    } catch (err) {
      console.warn(`Failed to fetch signature for user ${userId}:`, err);
      return null;
    }
  };

  // ✅ GROUP MRs BY DEPARTMENT + CONTROL NO
  const groupMRsByDeptAndControl = (mrListData) => {
    const grouped = {};

    mrListData.forEach(mr => {
      const dept = mr.department || mr.departmentName || 'N/A';
      const controlNo = mr.controlNo || mr.mrNo || 'N/A';
      const key = `${dept}|||${controlNo}`;

      if (!grouped[key]) {
        grouped[key] = {
          department: dept,
          controlNo: controlNo,
          mrs: [],
          names: [],
          allItems: [],
          status: mr.status,
          mrDate: mr.mrDate,
          arInvoiceNo: mr.arInvoiceNo
        };
      }

      grouped[key].mrs.push(mr);
      grouped[key].names.push({
        id: mr.id,
        name: mr.requestedByName,
        fullMR: mr
      });
      grouped[key].allItems.push(...(mr.mrDetails || []));
    });

    const groupedArray = Object.values(grouped);
    setGroupedMRs(groupedArray);
  };

  const fetchWebsiteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (!res.ok) throw new Error('Failed to fetch website info');
      const data = await res.json();
      const activeWebsite = data.find(w => w.isActive) || data[0];
      if (activeWebsite) {
        setWebsiteInfo(activeWebsite);
      }
    } catch (error) {
      console.error('Error fetching website info:', error);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid File',
        text: 'Please upload an image file (PNG, JPG, etc.)',
        confirmButtonColor: '#1e40af'
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'File Too Large',
        text: 'File size must be less than 2MB',
        confirmButtonColor: '#1e40af'
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomLogo(reader.result);
      setShowLogoUploader(false);
      Swal.fire({
        icon: 'success',
        title: 'Logo Uploaded!',
        text: 'Your custom logo has been set',
        timer: 2000,
        confirmButtonColor: '#10b981'
      });
    };
    reader.readAsDataURL(file);
  };
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try { setCurrentUser(JSON.parse(userData)); } catch { }
    }
  }, []);
  const handleResetLogo = () => {
    Swal.fire({
      title: 'Reset Logo?',
      text: 'This will restore the default company logo',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#1e40af',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reset it',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        setCustomLogo(null);
        setShowLogoUploader(false);
        Swal.fire({
          icon: 'success',
          title: 'Logo Reset!',
          text: 'Default logo restored',
          timer: 2000,
          confirmButtonColor: '#10b981'
        });
      }
    });
  };

  const getLogoSrc = () => {
    if (customLogo) return customLogo;
    return megalogo;
  };

  const filterMRs = () => {
    let filtered = [...mrList];

    if (searchTerm) {
      filtered = filtered.filter(mr =>
        mr.mrNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.controlNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.arInvoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.requestedByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.departmentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(mr => mr.status === parseInt(statusFilter));
    }

    if (departmentFilter !== 'all') {
      filtered = filtered.filter(mr =>
        mr.departmentName === departmentFilter ||
        mr.department === departmentFilter
      );
    }

    setFilteredMRs(filtered);
    setPaginationPage(1); // ← CHANGED


  };

  const getStatusBadge = (status) => {
    const badges = {
      '-1': { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
      '0': { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: FileText },
      '1': { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
      '2': { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
      '3': { label: 'Issued', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle },
      '4': { label: 'Cancelled', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle }
    };
    const badge = badges[String(status)] || badges['0'];
    const Icon = badge.icon;
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-bold ${badge.color} border inline-flex items-center gap-1.5`}>
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getUniqueDepartments = () => {
    const depts = mrList.map(mr => mr.department || mr.departmentName).filter(Boolean);
    return [...new Set(depts)].sort();
  };

  const getStatusCount = (status) => {
    return mrList.filter(mr => mr.status === status).length;
  };

  // ✅ VIEW GROUPED MR WITH NAME SELECTION
  const handleViewGroupedMR = async (group) => {
    // Set default selected name (first one)
    setSelectedNameForPDF(group.names[0]);

    // Create merged MR object
    const mergedMR = {
      ...group.names[0].fullMR,
      mrDetails: group.allItems,
      groupedNames: group.names,
      isGrouped: true
    };

    // 🔥 FETCH SIGNATURES
    const requestedBySignature = await fetchUserSignature(mergedMR.requestedBy);
    const approvedBySignature = await fetchUserSignature(mergedMR.approvedBy);
    const releasedBySignature = await fetchUserSignature(mergedMR.releasedBy);

    setSelectedMR({
      ...mergedMR,
      requestedBySignature,
      approvedBySignature,
      releasedBySignature
    });

    setShowModal(true);
  };

  const handleViewMR = async (mrId) => {
    try {
      const headerRes = await fetch(`${API_BASE_URL}api/MRHeader/${mrId}`);
      if (!headerRes.ok) {
        Swal.fire({ icon: 'error', title: 'Not Found', text: 'Could not load Material Request details', confirmButtonColor: '#1e40af' });
        return;
      }
      const headerData = await headerRes.json();
      const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mrId}`);
      let mrDetails = [];
      if (detailsRes.ok) mrDetails = await detailsRes.json();

      // 🔥 FETCH SIGNATURES
      const requestedBySignature = await fetchUserSignature(headerData.requestedBy);
      const approvedBySignature = await fetchUserSignature(headerData.approvedBy);
      const releasedBySignature = await fetchUserSignature(headerData.releasedBy);

      setSelectedMR({
        ...headerData,
        mrDetails: Array.isArray(mrDetails) ? mrDetails : [],
        isGrouped: false,
        requestedBySignature,
        approvedBySignature,
        releasedBySignature
      });

      setShowModal(true);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load MR details', confirmButtonColor: '#1e40af' });
    }
  };

  const handleDeleteMR = async (mr) => {
    const result = await Swal.fire({
      title: 'Delete Material Request?',
      html: `
      <div class="text-left">
        <p class="mb-2"><strong>Control No:</strong> ${mr.controlNo || 'N/A'}</p>
        <p class="mb-2"><strong>Requested By:</strong> ${mr.requestedByName}</p>
        <p class="mb-2"><strong>Department:</strong> ${mr.department || mr.departmentName}</p>
        <p class="mb-2"><strong>Items:</strong> ${mr.mrDetails?.length || 0}</p>
        <p class="mt-4 text-red-600 font-semibold">⚠️ This action cannot be undone!</p>
      </div>
    `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      // 1️⃣ DELETE ALL MR DETAILS FIRST
      const details = mr.mrDetails || [];
      for (const detail of details) {
        await fetch(`${API_BASE_URL}api/MRDetails/${detail.id}`, {
          method: 'DELETE'
        });
      }

      // 2️⃣ DELETE THE HEADER
      const res = await fetch(`${API_BASE_URL}api/MRHeader/${mr.id}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Failed to delete header');

      await Swal.fire({ icon: 'success', title: 'Deleted!', timer: 2000, confirmButtonColor: '#10b981' });
      fetchMRList();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Could not delete. Please try again.', confirmButtonColor: '#dc2626' });
    }
  };

  // ✅ EXPORT PDF WITH SELECTED NAME
  const exportToPDF = (mr) => {
    const doc = new jsPDF();
    const logoSrc = getLogoSrc();

    // Use selected name if grouped, otherwise use original
    const requestorName = mr.isGrouped && selectedNameForPDF
      ? selectedNameForPDF.name
      : mr.requestedByName;

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    let y = 10;

    // ✅ ADD LOGO AND COMPANY INFO
    if (logoSrc) {
      try {
        doc.addImage(logoSrc, 'PNG', 12, y, 25, 12);
      } catch (err) {
        console.error('Error adding logo to PDF:', err);
      }
    }

    // Company Info beside logo
    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100);
    if (websiteInfo?.websiteSlogan) {
      doc.setFont(undefined, 'italic');
      doc.text(websiteInfo.websiteSlogan, 40, y + 3);
      doc.setFont(undefined, 'normal');
    }
    if (websiteInfo?.websiteName) {
      doc.text(websiteInfo.websiteName, 40, y + 6);
    }
    if (websiteInfo?.websiteContact) {
      doc.text(websiteInfo.websiteContact, 40, y + 9);
    }
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text('Spare Parts Item Requests', 105, y + 8, { align: 'center' });

    doc.rect(170, y, 30, 12);
    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.text('Document Code:', 172, y + 5);
    doc.setFontSize(5.5);
    doc.text('SPR-F-04-SP-REQ.F-001', 172, y + 9);

    y += 15;



    doc.setLineWidth(0.3);
    // First Row: Date | Name | Good Issuance No.
    doc.rect(10, y, 35, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Date:', 12, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(formatDate(mr.mrDate), 12, y + 6);

    doc.rect(45, y, 70, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Name:', 47, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(requestorName || 'N/A', 47, y + 6); // ✅ USE SELECTED NAME

    doc.rect(115, y, 85, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Good Issuance No.:', 117, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(mr.arInvoiceNo || '—', 117, y + 6);
    y += 8;

    // Second Row: Department | Control No.
    doc.rect(10, y, 105, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Department:', 12, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(mr.department || mr.departmentName || 'N/A', 12, y + 6);

    doc.rect(115, y, 85, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Control No.:', 117, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(mr.controlNo || 'N/A', 117, y + 6);
    y += 8;

    doc.rect(10, y, 190, 6);
    doc.setFontSize(6);
    const dept1 = ['Mechanical', 'QC', 'Accounting', 'Logistics', 'Spareparts', 'Gen. Services', 'Production'];
    let xPos = 12;
    dept1.forEach(d => {
      doc.text(`${mr.department === d ? '[X]' : '[ ]'} ${d}`, xPos, y + 4);
      xPos += 27;
    });
    y += 6;

    doc.rect(10, y, 190, 6);
    const dept2 = ['RM Whse', 'Packaging', 'Electrical', 'FG Whse', 'Motorpool', 'Admin/HR/MIS', 'Fabrication'];
    xPos = 12;
    dept2.forEach(d => {
      doc.text(`${mr.department === d ? '[X]' : '[ ]'} ${d}`, xPos, y + 4);
      xPos += 27;
    });
    y += 8;

    if (mr.remarks) {
      doc.setFillColor(254, 243, 199);
      doc.rect(10, y, 190, 8, 'F');
      doc.rect(10, y, 190, 8);
      doc.setFontSize(6); doc.setFont(undefined, 'bold');
      doc.text('REMARKS:', 12, y + 3);
      doc.setFont(undefined, 'normal'); doc.setFontSize(5.5);
      doc.text(doc.splitTextToSize(mr.remarks, 175), 12, y + 6);
      y += 10;
    }

    const tableData = (mr.mrDetails || []).map((item) => [
      item.itemCode,
      item.requestedQty.toString(),
      item.uom,
      item.itemDescription.length > 45 ? item.itemDescription.substring(0, 45) + '...' : item.itemDescription,
      item.purpose || '—'
    ]);
    while (tableData.length < 6) tableData.push(['', '', '', '', '']);

    autoTable(doc, {
      startY: y,
      head: [['Item Code', 'Qty.', 'UOM', 'Item Description', 'Purpose']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [240, 240, 240], textColor: 0, fontSize: 7, fontStyle: 'bold', halign: 'left', lineWidth: 0.3, lineColor: 0, cellPadding: 2 },
      bodyStyles: { fontSize: 6.5, lineWidth: 0.3, lineColor: 0, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 25, halign: 'left', fontStyle: 'bold' },
        1: { cellWidth: 12, halign: 'center' },
        2: { cellWidth: 12, halign: 'center' },
        3: { cellWidth: 90, halign: 'left' },
        4: { cellWidth: 51, halign: 'left' }
      },
      margin: { left: 10, right: 10 }
    });

    const finalY = doc.lastAutoTable.finalY + 4;

    doc.rect(10, finalY, 95, 25);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Requested By:', 12, finalY + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(requestorName || 'N/A', 12, finalY + 16); // ✅ USE SELECTED NAME
    doc.line(12, finalY + 18, 100, finalY + 18);
    doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
    doc.text("(Requestor's Signature & Date)", 12, finalY + 22);
    doc.setTextColor(0, 0, 0);

    doc.rect(105, finalY, 95, 25);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Approved By:', 107, finalY + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    if (mr.approvedByName) {
      doc.text(mr.approvedByName, 107, finalY + 14);
      if (mr.approvedDate) doc.text(formatDate(mr.approvedDate), 107, finalY + 18);
    }
    doc.line(107, finalY + 18, 195, finalY + 18);
    doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
    doc.text("(Supervisor/Immediate Supervisor)", 107, finalY + 21);
    doc.text("Signature & Date", 107, finalY + 23.5);
    doc.setTextColor(0, 0, 0);

    const relY = finalY + 25;
    doc.rect(10, relY, 190, 25);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Released By:', 12, relY + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    if (mr.releasedByName) {
      doc.text(mr.releasedByName, 12, relY + 14);
      if (mr.releasedDate) doc.text(formatDate(mr.releasedDate), 12, relY + 18);
    }
    doc.line(12, relY + 18, 195, relY + 18);
    doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
    doc.text("(Spare Parts Personnel's) Signature & Date", 12, relY + 22);
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(6); doc.setTextColor(128, 128, 128);
    doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 287, { align: 'center' });
    doc.save(`SPR_${mr.controlNo || mr.mrNo}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const totalPages = Math.ceil(filteredMRs.length / itemsPerPage);
  const startIndex = (paginationPage - 1) * itemsPerPage; // ← CHANGED
  const endIndex = startIndex + itemsPerPage;
  const currentMRs = filteredMRs.slice(startIndex, endIndex);

  const logoSrc = getLogoSrc();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-lg shadow-blue-200">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Spare Parts Item Requests</h1>
                <p className="text-gray-500 mt-1">View and manage all material requests</p>
              </div>

            </div>
            <button
              onClick={() => setCurrentPage('ReturnRequestList')}  // ← GOODS NA TO!
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl transition-all shadow-lg hover:shadow-xl font-bold"
            >
              <RotateCcw className="w-5 h-5" />
              View Return Requests
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 hover:shadow-md transition-all">
              <FileText className="w-10 h-10 text-gray-500 mb-2" />
              <p className="text-gray-600 text-xs font-semibold uppercase tracking-wide mb-1">Draft</p>
              <p className="text-3xl font-bold text-gray-900">{getStatusCount(0)}</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-5 hover:shadow-md transition-all">
              <Clock className="w-10 h-10 text-amber-500 mb-2" />
              <p className="text-amber-700 text-xs font-semibold uppercase tracking-wide mb-1">Pending</p>
              <p className="text-3xl font-bold text-amber-900">{getStatusCount(1)}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-5 hover:shadow-md transition-all">
              <CheckCircle className="w-10 h-10 text-green-500 mb-2" />
              <p className="text-green-700 text-xs font-semibold uppercase tracking-wide mb-1">Approved</p>
              <p className="text-3xl font-bold text-green-900">{getStatusCount(2)}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5 hover:shadow-md transition-all">
              <CheckCircle className="w-10 h-10 text-blue-500 mb-2" />
              <p className="text-blue-700 text-xs font-semibold uppercase tracking-wide mb-1">Issued</p>
              <p className="text-3xl font-bold text-blue-900">{getStatusCount(3)}</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-5 hover:shadow-md transition-all">
              <XCircle className="w-10 h-10 text-red-500 mb-2" />
              <p className="text-red-700 text-xs font-semibold uppercase tracking-wide mb-1">Rejected</p>
              <p className="text-3xl font-bold text-red-900">{getStatusCount(-1)}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by Control No, Good Issuance No, Name, Department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white min-w-[180px] transition-all"
            >
              <option value="all">All Status</option>
              <option value="0">Draft</option>
              <option value="1">Pending</option>
              <option value="2">Approved</option>
              <option value="3">Issued</option>
              <option value="-1">Rejected</option>
            </select>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white min-w-[200px] transition-all"
            >
              <option value="all">All Departments</option>
              {getUniqueDepartments().map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ✅ GROUPED MRs SECTION */}
        {groupedMRs.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Grouped Item Requests</h2>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">
                {groupedMRs.filter(g => g.names.length > 1).length} Groups
              </span>
            </div>

            <div className="space-y-3">
              {groupedMRs
                .filter(group => group.names.length > 1) // Only show groups with multiple names
                .map((group, idx) => (
                  <div key={idx} className="border-2 border-blue-200 rounded-xl p-4 hover:shadow-md transition-all bg-blue-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <span className="font-bold text-gray-900">{group.department}</span>
                          <span className="text-gray-400">•</span>
                          <FileText className="w-4 h-4 text-blue-600" />
                          <span className="font-mono font-bold text-blue-900">{group.controlNo}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span className="font-semibold">{group.names.length} Requestors:</span>
                            <span>{group.names.map(n => n.name).join(', ')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            <span className="font-bold text-blue-700">{group.allItems.length} Total Items</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewGroupedMR(group)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                      >
                        <Eye className="w-4 h-4" />
                        View Grouped
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* MR Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
                <p className="text-gray-600 font-medium">Loading Material Requests...</p>
              </div>
            </div>
          ) : filteredMRs.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No material requests found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Name</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Good Issuance No.</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Department</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Control No.</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Items</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">

                    {currentMRs.map((mr) => (
                      <React.Fragment key={mr.id}>
                        {/* MAIN ROW */}
                        <tr className={`hover:bg-blue-50 transition-colors ${expandedRows[mr.id] ? 'bg-blue-50' : ''}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-900">{formatDate(mr.mrDate)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-900">{mr.requestedByName}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-mono font-bold text-blue-900">{mr.arInvoiceNo || '—'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-900">{mr.department || mr.departmentName || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                              <FileText className="w-3.5 h-3.5" />
                              {mr.controlNo || mr.mrNo}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200">
                              <Package className="w-3.5 h-3.5" />
                              {mr.mrDetails?.length || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex justify-center">
                              {getStatusBadge(mr.status)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2">
                              {/* ✅ ARROW TOGGLE BUTTON — para sa return history */}
                              <button
                                onClick={() => toggleReturnHistory(mr.id)}
                                className={`inline-flex items-center gap-1 px-3 py-2 rounded-xl transition-all shadow-md text-sm font-semibold border-2 ${expandedRows[mr.id]
                                  ? 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200'
                                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                  }`}
                                title="View Return History"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedRows[mr.id] ? 'rotate-180' : ''}`} />
                              </button>

                              <button
                                onClick={() => handleViewMR(mr.id)}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                              >
                                <Eye className="w-4 h-4" />
                                View
                              </button>

                              {(mr.status == 3 || mr.status == "3") && (
                                <button
                                  onClick={() => handleOpenReturnModal(mr)}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                  Return
                                </button>
                              )}

                              {currentUser?.positionId === 'SYSTEM ADMIN' && (
                                <button
                                  onClick={() => handleDeleteMR(mr)}
                                  className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* ✅ EXPANDABLE RETURN HISTORY ROW */}
                        {expandedRows[mr.id] && (
                          <tr>
                            <td colSpan={8} className="px-6 py-0 bg-orange-50 border-b border-orange-200">
                              <div className="py-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <RotateCcw className="w-4 h-4 text-orange-600" />
                                  <span className="text-sm font-bold text-orange-800">Return History for MR: {mr.controlNo || mr.mrNo}</span>
                                </div>

                                {loadingHistory[mr.id] ? (
                                  <div className="flex items-center gap-2 py-3 text-gray-500 text-sm">
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent"></div>
                                    Loading return history...
                                  </div>
                                ) : !returnHistory[mr.id] || returnHistory[mr.id].length === 0 ? (
                                  <div className="flex items-center gap-2 py-3 text-gray-500 text-sm">
                                    <AlertCircle className="w-4 h-4" />
                                    No return requests found for this MR.
                                  </div>
                                ) : (
                                  <table className="w-full text-sm rounded-xl overflow-hidden border border-orange-200">
                                    <thead className="bg-orange-100">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-bold text-orange-800 uppercase">Date</th>
                                        <th className="px-4 py-2 text-left text-xs font-bold text-orange-800 uppercase">Item Code</th>
                                        <th className="px-4 py-2 text-left text-xs font-bold text-orange-800 uppercase">Description</th>
                                        <th className="px-4 py-2 text-center text-xs font-bold text-orange-800 uppercase">Return Qty</th>
                                        <th className="px-4 py-2 text-left text-xs font-bold text-orange-800 uppercase">Reason</th>
                                        <th className="px-4 py-2 text-left text-xs font-bold text-orange-800 uppercase">Requested By</th>
                                        <th className="px-4 py-2 text-center text-xs font-bold text-orange-800 uppercase">Status</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-orange-100">
                                      {returnHistory[mr.id].map((ret) => (
                                        <tr key={ret.id} className="hover:bg-orange-50 transition-colors">
                                          <td className="px-4 py-2 text-gray-700">{formatDate(ret.requestedDate)}</td>
                                          <td className="px-4 py-2 font-mono font-bold text-blue-900">{ret.itemCode}</td>
                                          <td className="px-4 py-2 text-gray-700 max-w-xs truncate">{ret.itemDescription}</td>
                                          <td className="px-4 py-2 text-center">
                                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
                                              {ret.requestedReturnQty}
                                            </span>
                                          </td>
                                          <td className="px-4 py-2 text-gray-600 text-xs">{ret.returnReason || '—'}</td>
                                          <td className="px-4 py-2 text-gray-700">{ret.requestedByName}</td>
                                          <td className="px-4 py-2 text-center">{getReturnStatusBadge(ret.status)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-bold text-gray-900">{startIndex + 1}</span> to{' '}
                    <span className="font-bold text-gray-900">{Math.min(endIndex, filteredMRs.length)}</span> of{' '}
                    <span className="font-bold text-gray-900">{filteredMRs.length}</span> requests
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPaginationPage(prev => Math.max(1, prev - 1))}
                      disabled={paginationPage === 1}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, idx) => {
                        const pageNum = idx + 1;
                        if (pageNum === 1 || pageNum === totalPages || (pageNum >= paginationPage - 1 && pageNum <= paginationPage + 1)) { // ✅ FIXED
                          return (
                            <button key={pageNum} onClick={() => setPaginationPage(pageNum)} // ✅ FIXED
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${paginationPage === pageNum ? 'bg-blue-600 text-white shadow-md' : 'border border-gray-200 hover:bg-gray-100'}`}> {/* ✅ FIXED */}
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === paginationPage - 2 || pageNum === paginationPage + 2) { // ✅ FIXED
                          return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setPaginationPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={paginationPage === totalPages}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ✅ MODAL WITH NAME SELECTOR */}
      {showModal && selectedMR && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">

            {/* ✅ MODAL HEADER */}
            <div className="bg-white border-b-2 border-gray-400 px-8 py-6 rounded-t-2xl flex-shrink-0">
              <div className="flex items-start justify-between mb-4">

                {/* Left: Logo with Upload Feature */}
                <div className="flex items-center gap-3">
                  <div className="relative inline-block group">
                    {logoSrc ? (
                      <div className="flex flex-col items-center">
                        <img
                          src={logoSrc}
                          alt="Company Logo"
                          className="h-16 w-auto object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />

                        <div className="no-print absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          <button
                            onClick={() => setShowLogoUploader(true)}
                            className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all"
                            title="Change Logo"
                          >
                            <Upload size={16} />
                          </button>
                          {customLogo && (
                            <button
                              onClick={handleResetLogo}
                              className="p-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-all"
                              title="Reset to Default"
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => setShowLogoUploader(true)}
                        className="no-print h-20 w-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
                      >
                        <div className="text-center">
                          <Upload size={24} className="mx-auto text-gray-400 mb-1" />
                          <p className="text-xs text-gray-500">Upload Logo</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-sm text-gray-700 leading-relaxed">
                    {websiteInfo?.websiteSlogan && (<p className="italic font-medium mb-1">{websiteInfo.websiteSlogan}</p>)}
                    {websiteInfo?.websiteName && (<p className="font-semibold mb-1">{websiteInfo.websiteName}</p>)}
                    {websiteInfo?.websiteContact && (<p className="font-medium">{websiteInfo.websiteContact}</p>)}
                  </div>
                </div>

                {/* Center: Title */}
                <div className="text-center flex-1">
                  <h2 className="text-2xl font-bold text-gray-800">Spare Parts Item Requests</h2>
                  <p className="text-sm text-gray-600 mt-1">Document Code: <span className="font-semibold">SPR-F-04-SP-REQ.F-001</span></p>

                  {/* ✅ NAME SELECTOR FOR GROUPED MRs */}
                  {selectedMR.isGrouped && selectedMR.groupedNames && (
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-medium text-gray-600">Select Requestor:</span>
                      <select
                        value={selectedNameForPDF?.id || ''}
                        onChange={(e) => {
                          const selected = selectedMR.groupedNames.find(n => n.id === parseInt(e.target.value));
                          setSelectedNameForPDF(selected);
                        }}
                        className="px-3 py-1.5 border-2 border-blue-300 rounded-lg text-sm font-semibold text-blue-900 focus:border-blue-500 focus:outline-none bg-blue-50"
                      >
                        {selectedMR.groupedNames.map(nameObj => (
                          <option key={nameObj.id} value={nameObj.id}>
                            {nameObj.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Right: Control Number & Action Buttons */}
                <div className="flex items-start gap-3">
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-500">Document Code:</span>
                    <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg font-mono text-sm font-semibold mt-1">
                      <span className="font-semibold">SPR-F-04-SP-REQ.F-001</span>
                    </div>
                  </div>

                  <div className="no-print flex gap-2">
                    <button
                      onClick={() => exportToPDF(selectedMR)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Download PDF"
                    >
                      <Download className="h-5 w-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => setShowModal(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8 bg-white flex-1 overflow-y-auto">
              <div className="border-2 border-gray-300 mb-4">
                <div className="grid grid-cols-12 border-b-2 border-gray-300">
                  <div className="col-span-2 border-r-2 border-gray-300 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Date:</p>
                    <p className="text-sm text-gray-700">{formatDate(selectedMR.mrDate)}</p>
                  </div>
                  <div className="col-span-5 border-r-2 border-gray-300 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Name:</p>
                    <p className="text-sm text-gray-700">
                      {selectedMR.isGrouped && selectedNameForPDF
                        ? selectedNameForPDF.name
                        : selectedMR.requestedByName}
                    </p>
                  </div>
                  <div className="col-span-5 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Good Issuance No.:</p>
                    <p className="text-sm text-gray-700">{selectedMR.arInvoiceNo || '—'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-12 border-b-2 border-gray-300">
                  <div className="col-span-7 border-r-2 border-gray-300 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Department:</p>
                    <p className="text-sm text-gray-700">{selectedMR.department || selectedMR.departmentName || 'N/A'}</p>
                  </div>
                  <div className="col-span-5 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Control No.:</p>
                    <p className="text-sm text-gray-700">{selectedMR.controlNo || selectedMR.mrNo}</p>
                  </div>
                </div>
                <div className="p-3 border-b-2 border-gray-300">
                  <div className="grid grid-cols-7 gap-2 text-xs">
                    {['Mechanical', 'QC', 'Accounting', 'Logistics', 'Spareparts', 'Gen. Services', 'Production'].map(d => (
                      <label key={d} className="flex items-center gap-1">
                        <input type="checkbox" className="w-3 h-3" disabled checked={selectedMR.department === d} />
                        <span>{d}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="p-3">
                  <div className="grid grid-cols-7 gap-2 text-xs">
                    {['RM Whse', 'Packaging', 'Electrical', 'FG Whse', 'Motorpool', 'Admin/HR/MIS', 'Fabrication'].map(d => (
                      <label key={d} className="flex items-center gap-1">
                        <input type="checkbox" className="w-3 h-3" disabled checked={selectedMR.department === d} />
                        <span>{d}</span>
                      </label>
                    ))}
                  </div>
                  {selectedMR.branchSpecify && (
                    <div className="mt-2">
                      <span className="text-xs font-bold">Branch Specify: </span>
                      <span className="text-xs">{selectedMR.branchSpecify}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedMR.remarks && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                  <p className="text-xs text-amber-700 uppercase font-bold mb-1">REMARKS</p>
                  <p className="text-sm text-gray-800">{selectedMR.remarks}</p>
                </div>
              )}

              <div className="border-2 border-gray-300 overflow-hidden mb-4">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b-2 border-gray-300">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase border-r-2 border-gray-300">Item Code</th>
                      <th className="px-4 py-2 text-center text-xs font-bold text-gray-900 uppercase border-r-2 border-gray-300">Qty.</th>
                      <th className="px-4 py-2 text-center text-xs font-bold text-gray-900 uppercase border-r-2 border-gray-300">UOM</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase border-r-2 border-gray-300">Item Description</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedMR.mrDetails || []).map((item, index) => (
                      <tr key={item.id || index} className="border-b border-gray-300">
                        <td className="px-4 py-3 border-r-2 border-gray-300 text-sm font-bold text-gray-900">{item.itemCode}</td>
                        <td className="px-4 py-3 text-center border-r-2 border-gray-300 text-sm text-gray-900">{item.requestedQty}</td>
                        <td className="px-4 py-3 text-center border-r-2 border-gray-300 text-sm text-gray-900">{item.uom}</td>
                        <td className="px-4 py-3 border-r-2 border-gray-300 text-sm text-gray-900">{item.itemDescription}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.purpose || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-white border-t-2 border-gray-300 p-8 rounded-b-2xl flex-shrink-0">
              <div className="border-2 border-gray-300 p-4">
                <div className="grid grid-cols-3 gap-4">
                  {/* Requested By */}
                  <div>
                    <p className="text-xs font-bold text-gray-900 mb-2">Requested By:</p>

                    {/* 🔥 DISPLAY SIGNATURE IMAGE IF AVAILABLE - MOVED INSIDE BORDER */}
                    <div className="h-20 flex flex-col items-center justify-end border-b-2 border-gray-800 mb-2 pb-1">
                      {selectedMR.requestedBySignature && (
                        <img
                          src={selectedMR.requestedBySignature}
                          alt="Signature"
                          className="h-10 object-contain mb-1"
                        />
                      )}
                      <p className="text-sm font-bold text-gray-900 text-center">
                        {selectedMR.isGrouped && selectedNameForPDF
                          ? selectedNameForPDF.name
                          : selectedMR.requestedByName}
                      </p>
                    </div>
                    <p className="text-xs text-gray-600 italic text-center">(Requestor's Signature & Date)</p>
                  </div>

                  {/* Approved By */}
                  <div>
                    <p className="text-xs font-bold text-gray-900 mb-2">Approved By:</p>

                    {/* 🔥 DISPLAY SIGNATURE IMAGE IF AVAILABLE - MOVED INSIDE BORDER */}
                    <div className="h-20 flex flex-col items-center justify-end border-b-2 border-gray-800 mb-2 pb-1">
                      {selectedMR.approvedBySignature && (
                        <img
                          src={selectedMR.approvedBySignature}
                          alt="Signature"
                          className="h-10 object-contain mb-1"
                        />
                      )}
                      {selectedMR.approvedByName && (
                        <p className="text-sm font-bold text-gray-900 text-center">{selectedMR.approvedByName}</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 italic text-center">(Supervisor/Immediate Supervisor) Signature & Date</p>
                  </div>

                  {/* Released By */}
                  <div>
                    <p className="text-xs font-bold text-gray-900 mb-2">Released By:</p>

                    {/* Kung may releasedByName na (readonly/display mode) */}
                    {selectedMR.releasedByName ? (
                      <>
                        {/* 🔥 DISPLAY SIGNATURE IMAGE IF AVAILABLE - MOVED INSIDE BORDER */}
                        <div className="h-20 flex flex-col items-center justify-end border-b-2 border-gray-800 mb-2 pb-1">
                          {selectedMR.releasedBySignature && (
                            <img
                              src={selectedMR.releasedBySignature}
                              alt="Signature"
                              className="h-10 object-contain mb-1"
                            />
                          )}
                          <p className="text-sm font-bold text-gray-900 text-center">
                            {selectedMR.releasedByName}
                          </p>
                        </div>
                      </>
                    ) : (
                      /* Kung wala pa (editable/selection mode) - DROPDOWN WITH AUTO-SAVE */
                      <>
                        {/* 🔥 DROPDOWN AND PREVIEW IN ONE BOX */}
                        <div className="h-20 flex flex-col items-center justify-end border-b-2 border-gray-800 mb-2 pb-1">
                          {/* Show signature preview if selected */}
                          {selectedReleasedBy && (() => {
                            const user = sparepartsUsers.find(u => u.id === parseInt(selectedReleasedBy));
                            return user?.digitalSignFileBase64 ? (
                              <img
                                src={`data:${user.digitalSignContentType || 'image/png'};base64,${user.digitalSignFileBase64}`}
                                alt="Signature Preview"
                                className="h-10 object-contain mb-1"
                              />
                            ) : null;
                          })()}

                          {/* Dropdown or Name Display */}
                          {selectedReleasedBy ? (
                            <p className="text-sm font-bold text-gray-900 text-center">
                              {sparepartsUsers.find(u => u.id === parseInt(selectedReleasedBy))?.firstName}{' '}
                              {sparepartsUsers.find(u => u.id === parseInt(selectedReleasedBy))?.lastName}
                            </p>
                          ) : (
                            <select
                              value={selectedReleasedBy}
                              onChange={async (e) => {
                                const userId = e.target.value;
                                setSelectedReleasedBy(userId);

                                // 🔥 AUTO-SAVE: Update backend immediately!
                                if (userId) {
                                  const selectedUser = sparepartsUsers.find(u => u.id === parseInt(userId));
                                  if (selectedUser) {
                                    try {
                                      // 🔥 PREPARE SIGNATURE DATA
                                      const signatureData = selectedUser.digitalSignFileBase64
                                        ? `data:${selectedUser.digitalSignContentType || 'image/png'};base64,${selectedUser.digitalSignFileBase64}`
                                        : null;

                                      const response = await fetch(`${API_BASE_URL}api/MRHeader/${selectedMR.id}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          ...selectedMR,
                                          releasedBy: selectedUser.id,
                                          releasedByName: `${selectedUser.firstName} ${selectedUser.lastName}`,
                                          releasedBySignature: signatureData,
                                          releasedDate: new Date().toISOString()
                                        })
                                      });

                                      if (response.ok) {
                                        console.log('✅ Released By auto-saved:', selectedUser.firstName, selectedUser.lastName);

                                        // Update local state
                                        setSelectedMR(prev => ({
                                          ...prev,
                                          releasedBy: selectedUser.id,
                                          releasedByName: `${selectedUser.firstName} ${selectedUser.lastName}`,
                                          releasedBySignature: signatureData,
                                          releasedDate: new Date().toISOString()
                                        }));

                                        Swal.fire({
                                          icon: 'success',
                                          title: 'Saved!',
                                          html: `Released by: <strong>${selectedUser.firstName} ${selectedUser.lastName}</strong>`,
                                          timer: 1500,
                                          showConfirmButton: false
                                        });
                                      } else {
                                        throw new Error('Failed to save');
                                      }
                                    } catch (error) {
                                      console.error('❌ Error saving Released By:', error);
                                      Swal.fire({
                                        icon: 'error',
                                        title: 'Save Failed',
                                        text: 'Could not save Released By. Please try again.',
                                        confirmButtonColor: '#dc2626'
                                      });
                                    }
                                  }
                                }
                              }}
                              className="w-full px-3 py-1 border border-gray-400 rounded-lg text-sm font-semibold text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-blue-400 transition-all"
                            >
                              <option value="">-- Select Personnel --</option>
                              {sparepartsUsers.map((user) => (
                                <option key={user.id} value={user.id}>
                                  {user.firstName} {user.lastName}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </>
                    )}

                    <p className="text-xs text-gray-600 italic text-center">(Spare Parts Personnel's) Signature & Date</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button onClick={() => setShowModal(false)} className="px-6 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-all shadow-md">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logo Uploader Modal */}
      {showLogoUploader && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Upload Custom Logo</h3>
              <button
                onClick={() => setShowLogoUploader(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
                onClick={() => document.getElementById('logoInput').click()}
              >
                <Upload size={48} className="mx-auto text-gray-400 mb-3" />
                <p className="text-gray-700 font-medium mb-1">Click to upload logo</p>
                <p className="text-sm text-gray-500">PNG, JPG up to 2MB</p>
              </div>

              <input
                type="file"
                id="logoInput"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />

              {customLogo && (
                <div className="flex gap-2">
                  <button
                    onClick={handleResetLogo}
                    className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Reset to Default
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* 🔥 RETURN ITEMS MODAL */}
      {showReturnModal && selectedMRForReturn && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <RotateCcw className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Return Items</h3>
                    <p className="text-orange-100 text-sm mt-1">
                      MR No: {selectedMRForReturn.mrNo} | Control: {selectedMRForReturn.controlNo}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <strong>Enter quantity for items you want to return.</strong> Leave at 0 to skip.
                </p>
              </div>

              <div className="space-y-4">
                {returnItems.map((item) => (
                  <div key={item.id} className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-orange-300 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono font-bold text-lg text-blue-900">{item.itemCode}</span>
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                            Issued: {item.requestedQty} {item.uom}
                          </span>
                          {item.returnedQty > 0 && (
                            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
                              Already Returned: {item.returnedQty} {item.uom}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700 mb-2">{item.itemDescription}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Return Quantity</label>
                        <input
                          type="number"
                          min="0"
                          max={item.requestedQty - (item.returnedQty || 0)}
                          step="0.01"
                          value={item.returnQty}
                          onChange={(e) => updateReturnQty(item.id, e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none font-semibold text-lg"
                          placeholder="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Max: {item.requestedQty - (item.returnedQty || 0)} {item.uom}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Reason for Return</label>
                        <input
                          type="text"
                          value={item.returnReason}
                          onChange={(e) => updateReturnReason(item.id, e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
                          placeholder="e.g., Unused, Damaged..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t bg-gray-50 px-8 py-5 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <strong>{returnItems.filter(i => i.returnQty > 0).length}</strong> item(s) selected for return
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReturns}
                  disabled={returningItems || returnItems.every(i => i.returnQty === 0)}
                  className="px-8 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-xl font-bold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {returningItems ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-5 h-5" />
                      Submit Return Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
        
        @media print {
          .no-print {
            display: none !important;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          @page {
            margin: 1cm;
            size: A4;
          }
        }
      `}</style>
    </div>
  );
}